import { useState, useMemo } from "react";
import { Sparkles, Check, Search, X, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/**
 * Master species database. Each entry describes the minimum recommended
 * adult setup for a healthy captive specimen housed singly (UK hobbyist norms).
 *
 * Keys:
 *  - popular: appears in the "Most Popular" starter set
 *  - group: used for filtering tabs
 *  - habitat: purely informational
 *  - adult_min: minimum adult footprint in feet {w,h,d}
 *  - glass_mm: recommended glass thickness for wood/pvc builds
 *  - recommended_tier: preferred ReptiCraft build tier for this animal
 *  - note: 1-liner shown to the user so they know why
 */
const SPECIES = [
  // ───── POPULAR STARTER 8 ─────
  {
    id: "leopard_gecko",
    name: "Leopard Gecko",
    latin: "Eublepharis macularius",
    popular: true,
    group: "Geckos",
    habitat: "Terrestrial",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "engineered",
    note: "Ground-dweller. 4×2×2 ft is the modern UK minimum for a single adult.",
  },
  {
    id: "crested_gecko",
    name: "Crested Gecko",
    latin: "Correlophus ciliatus",
    popular: true,
    group: "Geckos",
    habitat: "Arboreal",
    adult_min: { w: 2, h: 3, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Arboreal — needs vertical height more than floor space.",
  },
  {
    id: "bearded_dragon",
    name: "Bearded Dragon",
    latin: "Pogona vitticeps",
    popular: true,
    group: "Dragons & Lizards",
    habitat: "Terrestrial",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "4×2×2 ft minimum; bigger is always better for basking/UVB gradient.",
  },
  {
    id: "blue_tongue_skink",
    name: "Blue Tongue Skink",
    latin: "Tiliqua scincoides",
    popular: true,
    group: "Dragons & Lizards",
    habitat: "Terrestrial",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Powerful diggers — solid front-opening enclosure recommended.",
  },
  {
    id: "royal_python",
    name: "Royal / Ball Python",
    latin: "Python regius",
    popular: true,
    group: "Snakes",
    habitat: "Terrestrial",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "4×2×2 ft for adults; PVC is also excellent for humidity retention.",
  },
  {
    id: "corn_snake",
    name: "Corn Snake",
    latin: "Pantherophis guttatus",
    popular: true,
    group: "Snakes",
    habitat: "Semi-arboreal",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "engineered",
    note: "Active climbers — provide branches. 4×2×2 ft is the UK minimum.",
  },
  {
    id: "hermanns_tortoise",
    name: "Hermann's Tortoise",
    latin: "Testudo hermanni",
    popular: true,
    group: "Tortoises",
    habitat: "Terrestrial",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Open-top tortoise table style; vented build keeps humidity low.",
  },
  {
    id: "african_fat_tailed_gecko",
    name: "African Fat-Tailed Gecko",
    latin: "Hemitheconyx caudicinctus",
    popular: true,
    group: "Geckos",
    habitat: "Terrestrial",
    adult_min: { w: 3, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "engineered",
    note: "Needs higher humidity than a leopard gecko — sealed build ideal.",
  },

  // ───── COMPREHENSIVE – GECKOS ─────
  {
    id: "gargoyle_gecko",
    name: "Gargoyle Gecko",
    latin: "Rhacodactylus auriculatus",
    popular: false,
    group: "Geckos",
    habitat: "Arboreal",
    adult_min: { w: 2, h: 3, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Arboreal — vertical enclosures suit best.",
  },
  {
    id: "leachianus_gecko",
    name: "Leachianus Gecko",
    latin: "Rhacodactylus leachianus",
    popular: false,
    group: "Geckos",
    habitat: "Arboreal",
    adult_min: { w: 2, h: 4, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Large arboreal gecko — tall enclosure essential.",
  },
  {
    id: "tokay_gecko",
    name: "Tokay Gecko",
    latin: "Gekko gecko",
    popular: false,
    group: "Geckos",
    habitat: "Arboreal",
    adult_min: { w: 2, h: 4, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Aggressive, strong — go tall and secure.",
  },
  {
    id: "day_gecko",
    name: "Giant Day Gecko",
    latin: "Phelsuma grandis",
    popular: false,
    group: "Geckos",
    habitat: "Arboreal",
    adult_min: { w: 2, h: 4, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Diurnal, UVB required; plenty of vertical space.",
  },

  // ───── DRAGONS / LIZARDS ─────
  {
    id: "uromastyx",
    name: "Uromastyx",
    latin: "Uromastyx spp.",
    popular: false,
    group: "Dragons & Lizards",
    habitat: "Terrestrial",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Arid species — very high basking temperatures required.",
  },
  {
    id: "chinese_water_dragon",
    name: "Chinese Water Dragon",
    latin: "Physignathus cocincinus",
    popular: false,
    group: "Dragons & Lizards",
    habitat: "Arboreal",
    adult_min: { w: 4, h: 6, d: 3 },
    glass_mm: 8,
    recommended_tier: "wood",
    note: "Large arboreal — needs a tall sealed enclosure and pool.",
  },
  {
    id: "green_iguana",
    name: "Green Iguana",
    latin: "Iguana iguana",
    popular: false,
    group: "Dragons & Lizards",
    habitat: "Arboreal",
    adult_min: { w: 6, h: 6, d: 3 },
    glass_mm: 10,
    recommended_tier: "wood",
    note: "Gets huge — walk-in enclosure or dedicated reptile room advised.",
  },
  {
    id: "veiled_chameleon",
    name: "Veiled Chameleon",
    latin: "Chamaeleo calyptratus",
    popular: false,
    group: "Dragons & Lizards",
    habitat: "Arboreal",
    adult_min: { w: 2, h: 4, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Needs excellent ventilation — consider a mesh-panel build.",
  },
  {
    id: "panther_chameleon",
    name: "Panther Chameleon",
    latin: "Furcifer pardalis",
    popular: false,
    group: "Dragons & Lizards",
    habitat: "Arboreal",
    adult_min: { w: 2, h: 4, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Airflow is crucial — ventilated wood build with UVB.",
  },
  {
    id: "green_anole",
    name: "Green Anole",
    latin: "Anolis carolinensis",
    popular: false,
    group: "Dragons & Lizards",
    habitat: "Arboreal",
    adult_min: { w: 2, h: 3, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Small arboreal — bioactive-friendly planted vivarium works well.",
  },
  {
    id: "sudan_plated_lizard",
    name: "Sudan Plated Lizard",
    latin: "Broadleysaurus major",
    popular: false,
    group: "Dragons & Lizards",
    habitat: "Terrestrial",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Hardy omnivore — good starter 'bigger' lizard.",
  },

  // ───── SNAKES ─────
  {
    id: "boa_constrictor",
    name: "Boa Constrictor",
    latin: "Boa constrictor",
    popular: false,
    group: "Snakes",
    habitat: "Semi-arboreal",
    adult_min: { w: 6, h: 2, d: 2 },
    glass_mm: 8,
    recommended_tier: "wood",
    note: "Large-bodied — 6×2×2 ft min; bigger for males/females.",
  },
  {
    id: "carpet_python",
    name: "Carpet Python",
    latin: "Morelia spilota",
    popular: false,
    group: "Snakes",
    habitat: "Arboreal",
    adult_min: { w: 5, h: 4, d: 2 },
    glass_mm: 8,
    recommended_tier: "wood",
    note: "Arboreal — tall enclosure with sturdy branches.",
  },
  {
    id: "burmese_python",
    name: "Burmese Python",
    latin: "Python bivittatus",
    popular: false,
    group: "Snakes",
    habitat: "Terrestrial",
    adult_min: { w: 8, h: 3, d: 3 },
    glass_mm: 10,
    recommended_tier: "wood",
    note: "Giant snake — custom-built room / outsized enclosure required.",
  },
  {
    id: "reticulated_python",
    name: "Reticulated Python",
    latin: "Malayopython reticulatus",
    popular: false,
    group: "Snakes",
    habitat: "Terrestrial",
    adult_min: { w: 8, h: 3, d: 3 },
    glass_mm: 10,
    recommended_tier: "wood",
    note: "Largest captive snake — experienced keepers only.",
  },
  {
    id: "hognose_snake",
    name: "Western Hognose",
    latin: "Heterodon nasicus",
    popular: false,
    group: "Snakes",
    habitat: "Terrestrial",
    adult_min: { w: 3, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "engineered",
    note: "Small burrower — deep substrate + secure front opening.",
  },
  {
    id: "kingsnake",
    name: "Kingsnake / Milksnake",
    latin: "Lampropeltis spp.",
    popular: false,
    group: "Snakes",
    habitat: "Terrestrial",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Escape artists — tight-fitting hinged doors recommended.",
  },
  {
    id: "green_tree_python",
    name: "Green Tree Python",
    latin: "Morelia viridis",
    popular: false,
    group: "Snakes",
    habitat: "Arboreal",
    adult_min: { w: 3, h: 4, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Strictly arboreal — perches at varied heights, high humidity.",
  },

  // ───── MONITORS & TEGUS ─────
  {
    id: "savannah_monitor",
    name: "Savannah Monitor",
    latin: "Varanus exanthematicus",
    popular: false,
    group: "Monitors & Tegus",
    habitat: "Terrestrial",
    adult_min: { w: 8, h: 4, d: 4 },
    glass_mm: 10,
    recommended_tier: "wood",
    note: "Huge & powerful — custom oversized enclosure essential.",
  },
  {
    id: "nile_monitor",
    name: "Nile Monitor",
    latin: "Varanus niloticus",
    popular: false,
    group: "Monitors & Tegus",
    habitat: "Semi-aquatic",
    adult_min: { w: 8, h: 4, d: 4 },
    glass_mm: 10,
    recommended_tier: "wood",
    note: "Large and aggressive — needs a pool and serious footprint.",
  },
  {
    id: "argentine_tegu",
    name: "Argentine Tegu",
    latin: "Salvator merianae",
    popular: false,
    group: "Monitors & Tegus",
    habitat: "Terrestrial",
    adult_min: { w: 6, h: 3, d: 3 },
    glass_mm: 10,
    recommended_tier: "wood",
    note: "Intelligent & strong — 6×3×3 ft absolute minimum for adults.",
  },
  {
    id: "ackie_monitor",
    name: "Ackie Monitor",
    latin: "Varanus acanthurus",
    popular: false,
    group: "Monitors & Tegus",
    habitat: "Terrestrial",
    adult_min: { w: 6, h: 2, d: 3 },
    glass_mm: 8,
    recommended_tier: "wood",
    note: "Dwarf monitor — deep substrate for burrowing.",
  },

  // ───── TORTOISES / CHELONIANS ─────
  {
    id: "horsfields_tortoise",
    name: "Horsfield's Tortoise",
    latin: "Testudo horsfieldii",
    popular: false,
    group: "Tortoises",
    habitat: "Terrestrial",
    adult_min: { w: 4, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Dry species — vented open-top tortoise table style.",
  },
  {
    id: "red_footed_tortoise",
    name: "Red-Footed Tortoise",
    latin: "Chelonoidis carbonarius",
    popular: false,
    group: "Tortoises",
    habitat: "Terrestrial",
    adult_min: { w: 6, h: 2, d: 3 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Tropical — higher humidity than Mediterranean tortoises.",
  },

  // ───── AMPHIBIANS ─────
  {
    id: "whites_tree_frog",
    name: "White's Tree Frog",
    latin: "Litoria caerulea",
    popular: false,
    group: "Amphibians",
    habitat: "Arboreal",
    adult_min: { w: 2, h: 3, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Arboreal amphibian — bioactive planted vivarium recommended.",
  },
  {
    id: "pacman_frog",
    name: "Pacman Frog",
    latin: "Ceratophrys spp.",
    popular: false,
    group: "Amphibians",
    habitat: "Terrestrial",
    adult_min: { w: 2, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "engineered",
    note: "Sit-and-wait ambush predator — small footprint is fine.",
  },
  {
    id: "dart_frog",
    name: "Dart Frog",
    latin: "Dendrobatidae",
    popular: false,
    group: "Amphibians",
    habitat: "Bioactive",
    adult_min: { w: 2, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Small bioactive builds — sealed glass front for humidity.",
  },
  {
    id: "fire_salamander",
    name: "Fire Salamander",
    latin: "Salamandra salamandra",
    popular: false,
    group: "Amphibians",
    habitat: "Terrestrial",
    adult_min: { w: 2, h: 2, d: 2 },
    glass_mm: 6,
    recommended_tier: "wood",
    note: "Cool temperatures, moist substrate, low light.",
  },
];

// Species with a published, verified care guide. Keep in sync with
// /app/backend/data/care_guides.py
const GUIDE_SPECIES = new Set([
  "leopard_gecko",
  "bearded_dragon",
  "crested_gecko",
  "royal_python",
  "corn_snake",
  "blue_tongue_skink",
  "hermanns_tortoise",
  "african_fat_tailed_gecko",
  "gargoyle_gecko",
  "leachianus_gecko",
  "tokay_gecko",
  "day_gecko",
  "uromastyx",
  "chinese_water_dragon",
  "green_iguana",
  "veiled_chameleon",
  "panther_chameleon",
  "green_anole",
  "sudan_plated_lizard",
  "boa_constrictor",
  "carpet_python",
  "burmese_python",
  "reticulated_python",
  "hognose_snake",
  "kingsnake",
  "green_tree_python",
  "savannah_monitor",
  "nile_monitor",
  "argentine_tegu",
  "ackie_monitor",
  "horsfields_tortoise",
  "red_footed_tortoise",
  "whites_tree_frog",
  "pacman_frog",
  "dart_frog",
  "fire_salamander",
]);

const GROUPS = [
  "All",
  "Geckos",
  "Dragons & Lizards",
  "Snakes",
  "Monitors & Tegus",
  "Tortoises",
  "Amphibians",
];

// Fixed engineered sizes (mirror of EngineeredConfiguratorPage)
const ENGINEERED_SIZE_KEYS = [
  { key: "2x2x2", w: 2, h: 2, d: 2 },
  { key: "3x2x2", w: 3, h: 2, d: 2 },
  { key: "4x2x2", w: 4, h: 2, d: 2 },
  { key: "5x2x2", w: 5, h: 2, d: 2 },
  { key: "6x2x2", w: 6, h: 2, d: 2 },
  { key: "2x3x2", w: 2, h: 3, d: 2 },
  { key: "3x3x2", w: 3, h: 3, d: 2 },
  { key: "4x3x2", w: 4, h: 3, d: 2 },
  { key: "5x3x2", w: 5, h: 3, d: 2 },
  { key: "6x3x2", w: 6, h: 3, d: 2 },
  { key: "2x4x2", w: 2, h: 4, d: 2 },
  { key: "3x4x2", w: 3, h: 4, d: 2 },
  { key: "4x4x2", w: 4, h: 4, d: 2 },
  { key: "5x4x2", w: 5, h: 4, d: 2 },
  { key: "6x4x2", w: 6, h: 4, d: 2 },
];

// Pick the engineered size that meets or exceeds minimums.
// Falls back to the largest available if the species is bigger than the range.
function pickEngineeredSizeKey({ w, h, d }) {
  const ok = ENGINEERED_SIZE_KEYS.filter((s) => s.w >= w && s.h >= h && s.d >= d);
  if (ok.length === 0) {
    return { key: "6x4x2", overflow: true };
  }
  // Smallest suitable
  ok.sort((a, b) => a.w * a.h * a.d - b.w * b.h * b.d);
  return { key: ok[0].key, overflow: false };
}

// Clamp helpers to match the wood/pvc slider ranges.
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function SpeciesWizard({ variant = "wood", onApply, triggerClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [group, setGroup] = useState("All");
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    let list = SPECIES;
    if (!showAll && group === "All" && !query) {
      list = list.filter((s) => s.popular);
    }
    if (group !== "All") list = list.filter((s) => s.group === group);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.latin.toLowerCase().includes(q) ||
          s.group.toLowerCase().includes(q)
      );
    }
    return list;
  }, [group, query, showAll]);

  const selected = SPECIES.find((s) => s.id === selectedId);

  // Build the recommendation payload for the current tier
  const buildRecommendation = (species) => {
    const { w, h, d } = species.adult_min;
    if (variant === "engineered") {
      const { key, overflow } = pickEngineeredSizeKey(species.adult_min);
      // Engineered glass range: 4mm (standard for small species), 6mm, 8mm, 10mm.
      // Small species recommended 6mm on wood -> drop to 4mm on engineered (the tier's standard).
      const engGlass = species.glass_mm <= 6 ? 4 : species.glass_mm;
      return {
        variant,
        speciesId: species.id,
        size_key: key,
        glass_thickness: engGlass,
        overflow,
        speciesName: species.name,
      };
    }
    if (variant === "pvc") {
      return {
        variant,
        speciesId: species.id,
        width: clamp(Math.ceil(w), 2, 8),
        height: clamp(Math.ceil(h), 2, 4),
        depth: clamp(Math.ceil(d), 2, 4),
        speciesName: species.name,
      };
    }
    // wood
    const heightCeil = Math.ceil(h);
    return {
      variant,
      speciesId: species.id,
      shape: "rectangular",
      width: clamp(Math.ceil(w), 2, 8),
      height: clamp(heightCeil, 2, 8),
      depth: clamp(Math.ceil(d), 2, 4),
      glass_thickness: species.glass_mm,
      speciesName: species.name,
    };
  };

  const handleApply = () => {
    if (!selected) return;
    const rec = buildRecommendation(selected);
    onApply?.(rec);
    setOpen(false);
    setSelectedId(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 border border-[#E07A5F]/40 bg-[#E07A5F]/10 hover:bg-[#E07A5F]/20 text-[#E07A5F] text-sm font-semibold uppercase tracking-wider transition-colors ${triggerClassName}`}
          data-testid="species-wizard-trigger"
        >
          <Sparkles className="w-4 h-4" />
          Not sure what you need? Pick your reptile
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-[#121212] border-white/10 text-[#F4F1DE]" data-testid="species-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#F4F1DE] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#E07A5F]" />
            Species Setup Wizard
          </DialogTitle>
          <DialogDescription className="text-[#F4F1DE]/60">
            Tell us what you're housing — we'll set sensible minimum dimensions and glass
            thickness for you. You can still tweak every value afterwards.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#F4F1DE]/40" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowAll(true);
            }}
            placeholder="Search by name or Latin name…"
            className="pl-9 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] placeholder:text-[#F4F1DE]/30"
            data-testid="species-wizard-search"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F4F1DE]/40 hover:text-[#F4F1DE]"
              data-testid="species-wizard-clear-search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Group tabs */}
        <div className="flex flex-wrap gap-1.5">
          {GROUPS.map((g) => (
            <button
              key={g}
              onClick={() => {
                setGroup(g);
                if (g !== "All") setShowAll(true);
              }}
              className={`px-2.5 py-1 text-xs border transition-colors ${
                group === g
                  ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]"
                  : "border-white/10 bg-[#0A0A0A] text-[#F4F1DE]/60 hover:text-[#F4F1DE]"
              }`}
              data-testid={`species-wizard-group-${g.replace(/\s|&/g, "").toLowerCase()}`}
            >
              {g}
            </button>
          ))}
        </div>

        {/* Popular vs All toggle (only visible in default view) */}
        {group === "All" && !query && (
          <div className="flex items-center justify-between text-xs text-[#F4F1DE]/60">
            <span className="uppercase tracking-widest">
              {showAll ? "Showing all species" : "Most popular"}
            </span>
            <button
              onClick={() => setShowAll((v) => !v)}
              className="text-[#E07A5F] hover:underline font-semibold"
              data-testid="species-wizard-toggle-all"
            >
              {showAll ? "Show popular only" : `Show all ${SPECIES.length} species →`}
            </button>
          </div>
        )}

        {/* Species grid */}
        <div className="overflow-y-auto flex-1 pr-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-[#F4F1DE]/50 py-6 text-center">
              No species match your search. Try a broader term.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filtered.map((s) => {
                const isSelected = selectedId === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    className={`text-left p-3 border transition-colors relative ${
                      isSelected
                        ? "border-[#E07A5F] bg-[#E07A5F]/10"
                        : "border-white/10 bg-[#0A0A0A] hover:border-white/30"
                    }`}
                    data-testid={`species-option-${s.id}`}
                  >
                    {isSelected && (
                      <Check className="w-4 h-4 text-[#E07A5F] absolute top-2 right-2" />
                    )}
                    <p className="text-sm font-semibold text-[#F4F1DE] leading-tight">{s.name}</p>
                    <p className="text-[10px] italic text-[#F4F1DE]/40 mb-1.5">{s.latin}</p>
                    <div className="flex flex-wrap gap-1 text-[10px]">
                      <span className="px-1.5 py-0.5 bg-white/5 text-[#F4F1DE]/60 uppercase tracking-wider">
                        {s.habitat}
                      </span>
                      <span className="px-1.5 py-0.5 bg-[#E07A5F]/10 text-[#E07A5F] uppercase tracking-wider">
                        {s.adult_min.w}×{s.adult_min.h}×{s.adult_min.d} ft
                      </span>
                      <span className="px-1.5 py-0.5 bg-amber-500/10 text-amber-400 uppercase tracking-wider">
                        {s.glass_mm}mm glass
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected preview */}
        {selected && (
          <div
            className="border border-[#E07A5F]/40 bg-[#E07A5F]/5 p-3 text-xs text-[#F4F1DE]/80"
            data-testid="species-wizard-preview"
          >
            <p className="text-[#E07A5F] font-semibold mb-1">
              Recommended setup for {selected.name}
            </p>
            <p className="text-[#F4F1DE]/70 mb-2">{selected.note}</p>
            {variant === "engineered" && selected.adult_min.w > 6 && (
              <p className="text-amber-400 mb-2">
                ⚠ This species is larger than our Engineered range. Consider the Wood tier for a
                custom build.
              </p>
            )}
            {variant === "pvc" && (selected.adult_min.w > 8 || selected.adult_min.h > 4) && (
              <p className="text-amber-400 mb-2">
                ⚠ This species exceeds PVC size limits. Wood is the recommended tier.
              </p>
            )}
            {selected.recommended_tier !== variant && selected.recommended_tier !== "any" && (
              <p className="text-[#F4F1DE]/50 italic">
                Tip: our team actually recommends the{" "}
                <span className="text-[#E07A5F] font-semibold capitalize">
                  {selected.recommended_tier}
                </span>{" "}
                tier for this species.
              </p>
            )}
            {GUIDE_SPECIES.has(selected.id) && (
              <Link
                to={`/guides/${selected.id.replace(/_/g, "-")}`}
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center gap-1.5 text-[#E07A5F] hover:text-[#F5A593] font-semibold text-xs"
                data-testid="species-wizard-guide-link"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Read our {selected.name} care guide →
              </Link>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="border-white/20 text-[#F4F1DE]"
            data-testid="species-wizard-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!selected}
            className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white disabled:opacity-40"
            data-testid="species-wizard-apply"
          >
            Apply recommendation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
